import type { LibraryPageSize } from "@/core/services/types/library-pagination.types";
import type { LibrarySection } from "@/lib/library-search-snapshot";

export type LibraryListPageSnapshot<T> = {
  fingerprint: string;
  items: T[];
  total: number;
  page: number;
  pageSize: LibraryPageSize;
};

const STORAGE_PREFIX = "whart:library-results-freeze:";

function storageKey(section: LibrarySection): string {
  return `${STORAGE_PREFIX}${section}`;
}

export function getLibraryResultsFreeze<T>(
  section: LibrarySection,
): LibraryListPageSnapshot<T> | null {
  if (typeof sessionStorage === "undefined") return null;

  try {
    const raw = sessionStorage.getItem(storageKey(section));
    if (!raw) return null;
    return JSON.parse(raw) as LibraryListPageSnapshot<T>;
  } catch {
    return null;
  }
}

export function setLibraryResultsFreeze<T>(
  section: LibrarySection,
  snapshot: LibraryListPageSnapshot<T>,
): void {
  if (typeof sessionStorage === "undefined") return;

  try {
    sessionStorage.setItem(storageKey(section), JSON.stringify(snapshot));
  } catch {
    // Ignore quota errors and private browsing restrictions.
  }
}

export function clearLibraryResultsFreeze(section: LibrarySection): void {
  if (typeof sessionStorage === "undefined") return;

  try {
    sessionStorage.removeItem(storageKey(section));
  } catch {
    // Ignore storage access errors.
  }
}

export function isLibraryResultsFrozen(
  section: LibrarySection,
  queryFingerprint: string,
): boolean {
  const stored = getLibraryResultsFreeze(section);
  return stored?.fingerprint === queryFingerprint;
}

export function getLibraryListQueryKeyPrefix(
  section: LibrarySection,
): string {
  return `${section}-library-list`;
}
